import * as d3 from 'd3'
import { useD3, type D3Svg, type D3Dims } from './useD3.js'
import { useChartTooltip, type TooltipVariant } from './useChartTooltip.js'
import { ChartLegend } from './ChartLegend.js'
import { CHART_COLORS } from './chartTokens.js'
import styles from './LineChart.module.css'

/**
 * SpendRevenueRoasChart — 광고 성과 콤보 차트.
 *
 * 일자별로 [광고비 스택바(네이버+인스타) | 인스타 전환매출 바]를 나란히 그리고,
 * 우측 보조 y축(%)에 ROAS 라인(자사 실결제 기준)을 얹는다. 좌축=금액(원), 우축=ROAS(%).
 * 네이버(스마트스토어)는 전환매출을 측정할 수 없어 전환매출 스택에서 제외한다.
 * 기존 D3 래퍼 관례(useD3/useChartTooltip/ChartLegend/chartTokens)를 따른다.
 */

export interface SpendRevenueRoasDatum {
  date: string
  spendMeta: number
  spendNaver: number
  revMeta: number
  roas: number | null // 0~1 비율 (예: 0.495 = 49.5%)
}

interface Props {
  data: SpendRevenueRoasDatum[]
  className?: string
  tooltipVariant?: TooltipVariant
}

// PDF 팔레트: 네이버=녹색 계열, 인스타=분홍 계열. 광고비=연한색(위), 전환매출=진한색.
// 전환매출은 인스타(META)만 — 네이버는 측정 불가.
const COLORS = {
  spendNaver: CHART_COLORS.successLight, // 연녹
  spendMeta: '#FBCFE8',                   // 연분홍
  revMeta: '#EC4899',                     // 진분홍
  roas: '#111827',                        // 검정(라인)
}

const MARGIN = { top: 16, right: 52, bottom: 44, left: 56 }
const AXIS_LINE = '#E8E4DF'
const AXIS_TEXT = '#9E9996'
const HIGHLIGHT_BAND = 'rgba(148, 163, 184, 0.12)' // 호버 열 배경 (slate, 옅게)

interface TooltipCtx {
  show: (x: number, y: number, text: string) => void
  hide: () => void
}

function fmtWon(v: number): string {
  return `₩${Math.round(v).toLocaleString()}`
}
function fmtPct(r: number | null): string {
  return r == null ? '-' : `${(r * 100).toFixed(1)}%`
}

function renderChart(svg: D3Svg, dims: D3Dims, data: SpendRevenueRoasDatum[], tt?: TooltipCtx) {
  const w = dims.width - MARGIN.left - MARGIN.right
  const h = dims.height - MARGIN.top - MARGIN.bottom
  if (w <= 0 || h <= 0 || data.length === 0) return

  const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  // 호버 강조용 참조: 날짜 → ROAS 점 circle
  const roasDots = new Map<string, d3.Selection<SVGCircleElement, unknown, null, undefined>>()

  // x0: 일자, x1: 그룹(광고비/매출)
  const x0 = d3.scaleBand<string>().domain(data.map((d) => d.date)).range([0, w]).padding(0.28)
  const x1 = d3.scaleBand<string>().domain(['spend', 'rev']).range([0, x0.bandwidth()]).padding(0.12)

  // 좌축: 금액 (광고비 합·인스타 전환매출 중 최댓값)
  const maxAmount = d3.max(data, (d) => Math.max(d.spendMeta + d.spendNaver, d.revMeta)) ?? 0
  const yL = d3.scaleLinear().domain([0, Math.max(1, maxAmount)]).nice().range([h, 0])

  // 우축: ROAS(%). 데이터 최댓값에 15% 여유를 두고 nice()로 깔끔한 상한. (100% 고정 금지 —
  // 축이 데이터보다 과도하게 크면 라인이 막대에 파묻혀 추세가 안 읽힘.) 최소 상한 0.1(10%).
  const maxRoas = d3.max(data, (d) => d.roas ?? 0) ?? 0
  const yR = d3.scaleLinear().domain([0, Math.max(0.1, maxRoas * 1.15)]).nice().range([h, 0])

  // x축(일자)
  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(x0).tickSize(0).tickPadding(10))
    .call((el) => el.select('.domain').attr('stroke', AXIS_LINE))
    .selectAll('text')
    .attr('fill', AXIS_TEXT)
    .attr('font-size', '11px')

  // 좌축(금액) — 천 단위 k 축약
  g.append('g')
    .call(
      d3.axisLeft(yL).ticks(5).tickFormat((v) => {
        const n = Number(v)
        return n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`
      }).tickSize(-w).tickPadding(8),
    )
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', AXIS_LINE).attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', AXIS_TEXT)
    .attr('font-size', '11px')

  // 우축(ROAS %)
  g.append('g')
    .attr('transform', `translate(${w},0)`)
    .call(d3.axisRight(yR).ticks(5).tickFormat((v) => `${Math.round(Number(v) * 100)}%`).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').attr('stroke', AXIS_LINE))
    .selectAll('text')
    .attr('fill', AXIS_TEXT)
    .attr('font-size', '11px')

  // 호버 하이라이트 밴드 — 마우스 올린 날짜 열 뒤에 깔리는 옅은 배경. 초기 숨김.
  const step = x0.step()
  const bandPad = 4
  const highlightBand = g.append('rect')
    .attr('y', 0)
    .attr('height', h)
    .attr('width', step - bandPad)
    .attr('rx', 6)
    .attr('fill', HIGHLIGHT_BAND)
    .style('opacity', 0)
    .style('pointer-events', 'none')

  // 스택 헬퍼: base(아래 채널) + top(위 채널) 두 rect (지정된 그룹에 그린다)
  const drawStack = (
    container: d3.Selection<SVGGElement, unknown, null, undefined>,
    xPos: number,
    bottomVal: number,
    topVal: number,
    bottomColor: string,
    topColor: string,
  ) => {
    const bw = x1.bandwidth()
    // 아래(네이버)
    if (bottomVal > 0) {
      container.append('rect')
        .attr('x', xPos)
        .attr('y', yL(bottomVal))
        .attr('width', bw)
        .attr('height', h - yL(bottomVal))
        .attr('fill', bottomColor)
        .attr('rx', 2)
    }
    // 위(인스타) — 아래 위에 누적
    if (topVal > 0) {
      const stackTop = bottomVal + topVal
      container.append('rect')
        .attr('x', xPos)
        .attr('y', yL(stackTop))
        .attr('width', bw)
        .attr('height', yL(bottomVal) - yL(stackTop))
        .attr('fill', topColor)
        .attr('rx', 2)
    }
  }

  // 날짜별 막대를 열 그룹으로 묶는다 → 호버 시 다른 열을 흐리게(dim) 처리.
  const colGroups = new Map<string, d3.Selection<SVGGElement, unknown, null, undefined>>()
  data.forEach((d) => {
    const gx = x0(d.date)
    if (gx == null) return
    const colG = g.append('g')
    colGroups.set(d.date, colG)
    const spendX = gx + (x1('spend') ?? 0)
    const revX = gx + (x1('rev') ?? 0)
    // 광고비 스택 (네이버+인스타)
    drawStack(colG, spendX, d.spendNaver, d.spendMeta, COLORS.spendNaver, COLORS.spendMeta)
    // 전환매출 — 인스타만 (네이버는 측정 불가). 단일 막대로 그린다.
    drawStack(colG, revX, 0, d.revMeta, COLORS.revMeta, COLORS.revMeta)
  })

  // ROAS 라인 (우축) — roas 값이 있는 점만 연결
  const linePoints = data.filter((d) => d.roas != null)
  const line = d3.line<SpendRevenueRoasDatum>()
    .x((d) => (x0(d.date) ?? 0) + x0.bandwidth() / 2)
    .y((d) => yR(d.roas ?? 0))
    .curve(d3.curveMonotoneX)

  if (linePoints.length > 0) {
    g.append('path')
      .datum(linePoints)
      .attr('fill', 'none')
      .attr('stroke', COLORS.roas)
      .attr('stroke-width', 2)
      .attr('d', line)

    // 날짜별 ROAS 점 (호버 시 확대용으로 date→circle 참조 보관)
    linePoints.forEach((d) => {
      const dot = g.append('circle')
        .attr('cx', (x0(d.date) ?? 0) + x0.bandwidth() / 2)
        .attr('cy', yR(d.roas ?? 0))
        .attr('r', 3.5)
        .attr('fill', COLORS.roas)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('pointer-events', 'none')
      roasDots.set(d.date, dot)
    })
  }

  // 툴팁 + 호버 강조 — 일자 컬럼 전체를 덮는 투명 hit area
  if (tt) {
    // 특정 날짜 열만 강조: 배경 밴드 표시 + 그 열 외 나머지 dim + ROAS 점 확대
    const focusColumn = (date: string) => {
      const gx = x0(date)
      if (gx != null) {
        highlightBand.attr('x', gx - (step - x0.bandwidth()) / 2 + bandPad / 2).style('opacity', 1)
      }
      colGroups.forEach((sel, key) => sel.style('opacity', key === date ? 1 : 0.35))
      roasDots.forEach((sel, key) => sel.attr('r', key === date ? 5.5 : 3.5))
    }
    const clearFocus = () => {
      highlightBand.style('opacity', 0)
      colGroups.forEach((sel) => sel.style('opacity', 1))
      roasDots.forEach((sel) => sel.attr('r', 3.5))
    }

    g.selectAll('.hit')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', (d) => (x0(d.date) ?? 0) - (step - x0.bandwidth()) / 2)
      .attr('y', 0)
      .attr('width', step)
      .attr('height', h)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mouseover mousemove', (event, d) => {
        focusColumn(d.date)
        const totalSpend = d.spendMeta + d.spendNaver
        const text = [
          d.date,
          `광고비: ${fmtWon(totalSpend)}`,
          `  인스타: ${fmtWon(d.spendMeta)}`,
          `  네이버: ${fmtWon(d.spendNaver)}`,
          `인스타 전환매출: ${fmtWon(d.revMeta)}`,
          `ROAS(실결제): ${fmtPct(d.roas)}`,
        ].join('\n')
        tt.show(event.clientX + 12, event.clientY - 10, text)
      })
      .on('mouseout', () => {
        clearFocus()
        tt.hide()
      })
  }
}

const LEGEND_ITEMS = [
  { label: '네이버 광고비', color: COLORS.spendNaver },
  { label: '인스타 광고비', color: COLORS.spendMeta },
  { label: '인스타 전환매출', color: COLORS.revMeta },
  { label: 'ROAS(실결제, %)', color: COLORS.roas },
]

export function SpendRevenueRoasChart({ data, className, tooltipVariant }: Props) {
  const { tooltipElement, show, hide } = useChartTooltip(tooltipVariant)

  const ref = useD3(
    (svg, dims) => {
      renderChart(svg, dims, data, { show, hide })
    },
    [data],
  )

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <svg ref={ref} className={styles.svg} />
      {tooltipElement}
      <ChartLegend items={LEGEND_ITEMS} />
    </div>
  )
}
